import FavoriteIcon from '@mui/icons-material/Favorite';
import GridViewIcon from '@mui/icons-material/GridView';
import ListSharpIcon from '@mui/icons-material/ListSharp';
import { IconButton, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, ButtonGroup, Stack } from 'react-bootstrap';
import { Configure, CurrentRefinements, InstantSearch, Pagination, RangeInput, RefinementList, SearchBox, SortBy, Stats } from 'react-instantsearch';
import { useHistory, useLocation } from 'react-router';
import Cookies from 'universal-cookie';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import '../../App.css';
import WhiskeyBarrel from '../../assets/images/products/WhiskyBarrel.png';
import "../../sass/responsive.scss";
import { CommonService } from '../../Service/CommonService';
import { OrderApiService } from '../../Service/OrderApiService';
import { searchClient } from "../../typesenseAdapter.js";
import '../Dynamic/Typesense.css';
import '../Dynamic/Typesense.scss';
import { ClearFilters } from '../Widgets/ClearFilters';
import { AgeRangeSlider } from './AgeRangeSlider';
import HitsContainer from './HitsContainer';
import HitsTableContainer from './HitsTableContainer';
import { PriceRangeSlider } from './PriceRangeSlider.js';
import SearchContainer from './SearchContainer';
import { Collapsible } from './Collapsible';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SwapVertIcon from '@mui/icons-material/SwapVert';
import FilterIcon from "../../assets/images/icons/FilterIcon.svg";

const Typesense: React.FC = () => {
    const closeOnChange = () => window.innerWidth > 375;
    const location = useLocation();
    const currentPath = location.pathname;
    const [gridView, setView] = useState<boolean>(true);

    const [soldBarrels, setSoldBarrels] = useState([]);

    const [wishlistItems, setWishlistItems] = useState<any[]>([]);
    const [isShowAssociatedCompany, setShowAssociatedCompany] = useState(false);
    const [isShowViewMyListingsCompany, setShowViewMyListingsCompany] = useState(false);
    const userId = new Cookies().get('userId');
    const companyId = new Cookies().get('companyId');
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const collectionName = process.env.REACT_APP_TYPESENSE_COLLECTIONNAME; //Typesense collection name

    const getUserWishlist = async () => {
        try {
            const response: any = await OrderApiService.getUserWishlist(userId);
            setWishlistItems(response.data);
        } catch (error) {
            console.error('Error while fetching wishlist:', error);
        }
    };
    const history = useHistory();

    // Handle toggle change
    const handleToggle = (e) => {
      const newValue = e.target.checked;
      setShowAssociatedCompany(newValue);
      localStorage.setItem('showAssociatedCompany', newValue ? 'yes' : 'no');
    };

    const handleViewMyListingToggle = (e) => {
      const newValue = e.target.checked;
      setShowViewMyListingsCompany(newValue);
      localStorage.setItem('showViewMyListingsCompany', newValue ? 'yes' : 'no');
    };

    useEffect(() => {
      if(CommonService.isUserLoggedIn()) {
        getUserWishlist();
      }

      const storedViewMyListingValue = localStorage.getItem('showViewMyListingsCompany');
      if (storedViewMyListingValue) {
        setShowViewMyListingsCompany(storedViewMyListingValue === 'yes');
      }

      const storedValue = localStorage.getItem('showAssociatedCompany');
      if (storedValue) {
        setShowAssociatedCompany(storedValue === 'yes');
      }
    }, []);

    useEffect(() => {

          let filters: string[] = [];
          if (currentPath === '/user/inventories') {
            if(isShowViewMyListingsCompany) {
              filters = [`company_id:=${companyId}`];
            } 
          } else if (currentPath === '/user/wish-list') {
            if (wishlistItems.length > 0) {
                filters.push(`owner_id:!=${userId}`);
                filters.push(`(${wishlistItems.map(id => `inventory_id:${id}`).join(' || ')})`);
            } else {
                filters.push(`owner_id:!=${userId}`, `inventory_id:null`);
            }
            if(CommonService.isUserLoggedIn()) {
                getAllSoldInventoryWishList();
            }
        }

        // only active inventory show in search result
        filters.push("is_active:=true", "is_delete:=false");

        // Generate dynamic filter string
        const filterValue = filters.join(' && ').replace(/\\/g, '');
        setGlobalFilter(filterValue);

    }, [location.pathname, wishlistItems, isShowViewMyListingsCompany]);

    const switchView: any = (view: any) => {
        if (view == 'grid')
            setView(true);
        else
            setView(false);
    };

    const getAllSoldInventoryWishList = async() => {
        try {
            const response = await OrderApiService.getAllSoldInventoryWishList(CommonService.getUserId());
            setSoldBarrels(response.data);
        } catch (error) {
            console.error('Error while fetching Container availability:', error);
            return false; // Return false in case of an error
        }
    };

    const removeFromWishList = async (itemId: string) => {
        const wishListRequest = {
            inventoryId: itemId,
            userId: userId,
        };

        try {
            const response: any = await OrderApiService.removeFromWishlist(wishListRequest);
            getUserWishlist();
        } catch (error) {
            console.error('Error while removing wishlist data:', error);
        }
    };

    const removeFromWishListById = async (wishListId: any) => {
        
        try {
            const response: any = await OrderApiService.removeFromWishlistById(wishListId);
            getAllSoldInventoryWishList();
        } catch (error) {
            console.error('Error while removing wishlist data:', error);
        }
    };

    // Transform refinement items using calculateAge
    function transformRefinementItems(items: any) {
      return items.map((item: any) => {

        // Remove underscores from the attribute name
        const attributeName = item.attribute.replace(/_/g, ' ');
        item.label = attributeName; // Directly modify the item
    
        if (attributeName === 'fill date timestamp') {
          return {
            ...item,
            refinements: item.refinements.map((refinement: any) => {
              // Match relational operators and numeric timestamp
              const match = refinement.label.match(/([<>=]*\s*)(\d+)/);
              if (match && match.length >= 3) {
                const prefix = refinement.operator; // Relational operator (e.g., <, >, <=, >=, ==)
                const timestamp = parseInt(match[2], 10); // Numeric timestamp value
                const { years, months } = CommonService.calculateAge(new Date(timestamp).toISOString());
    
                // Create labels for years and months
                const yearLabel = years > 1 ? `${years} years` : `${years} year`;
                const monthLabel = months > 1 ? `${months} months` : `${months} month`;
    
                // Preserve the operator and display the transformed label
                return {
                  ...refinement,
                  label: `${prefix}${yearLabel}, ${monthLabel}`
                };
              }
              return refinement; // Return unchanged if no match
            })
          };
        }

        return item; // ✅ Return item directly, not { item }
      });
    }
    
    const headerRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const bodyTable = bodyRef.current;
        const headerTable = headerRef.current;

        const handleScroll = () => {
            if (headerTable && bodyTable) {
                headerTable.scrollLeft = bodyTable.scrollLeft;
            }
        };

        if (bodyTable) {
            bodyTable.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (bodyTable) {
                bodyTable.removeEventListener('scroll', handleScroll);
            }
        };
    }, []);

    return (
        <div className={`Typesense__container ${CommonService.isAdmin() && "admin-typesense__container"}`}>
          {CommonService.isAdmin() && (<div className="header-wrapper d-flex align-items-center gap-2 flex-wrap justify-content-between">
            <>
            <div className="d-flex align-items-center gap-2">
              <div className="back__button" onClick={() => history.goBack()}>
                <IconButton
                  aria-label="ArrowBackIcon"
                  sx={{ backgroundColor: "#FFF" }}
                >
                  <ArrowBackIcon sx={{ color: "#1C1B1F" }} />
                </IconButton>
              </div><Typography
                variant="h1"
                gutterBottom
                className='mb-0'
                sx={{
                  fontSize: "24px",
                  fontWeight: 700,
                  color: "#242424",
                  lineHeight: "22px",
                }}
              >
                Marketplace
              </Typography>
            </div>
            <div className="view__type--select">
                <ButtonGroup>
                  <Button variant="link"
                    aria-label="View as" disabled
                    className='d-none d-md-block'
                  >
                    View As
                  </Button>
                  <Button aria-label="Grid" className='ms-1 ms-sm-0 me-2'
                    onClick={() => {
                      switchView("grid");
                    }} style={{ backgroundColor: gridView === true ? '#0C3344' : '#D7D7D7' }} >
                    <GridViewIcon style={{ color: gridView === true ? 'white' : 'inherit' }} />
                  </Button>
                  <Button
                    aria-label="Table"
                    onClick={() => {
                      switchView("table");
                    }}
                    style={{ backgroundColor: gridView === false ? '#0C3344' : '#D7D7D7' }} >
                    <ListSharpIcon style={{ color: gridView === false ? 'white' : 'inherit' }} />
                  </Button>
                </ButtonGroup>
              </div>
            </>
          </div>
          )}
          <div className='typesense-inner'>
              {/* <Container maxWidth="sm" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '350px' }}>
                  <Spinner animation="border" variant="secondary" className="mt-3" />
                  <Typography variant="subtitle1" sx={{ fontSize: '20px', fontWeight: 600, color: '#737376', marginTop: '12px' }}>
                      Product details is loading...
                  </Typography>
              </Container> */}
              <InstantSearch searchClient={searchClient} indexName={collectionName}>
                  <Configure filters={globalFilter} /> {/* Use the state variable directly here */}
                  <div className="container">
                      
                      <div className="row">
                          <div className="col-12 col-md-6 col-lg-3 mb-5 mb-lg-0">
                              <div className="Typesense__filters-wrapper d-flex flex-wrap justify-content-center align-items-center pt-4 pb-5 w-100">
                                  <div className="filters--type d-flex flex-column w-100">
                                      <div className="filter px-3 py-2 p-md-3">
                                          <SearchBox placeholder="Search for inventories... " />
                                      </div>
                                        {/* FILTERS TITLE */}
                                        <div className="filter px-3 py-2 p-md-3 border-bottom">
                                          <div className="filter--title d-flex align-items-center gap-2">
                                            <img src={FilterIcon} className="cursor-pointer" alt="FilterIcon" />
                                            <span>Filters</span>
                                          </div>
                                        </div>

                                      {/* MATCHES + CURRENT REFINEMENTS */}
                                      {/* {<ClearFilters /> } */}
                                      <div className='d-flex justify-content-end px-3'>
                                      {/* <button className='clear-filter-btn'> <CloseIcon /> <span>Clear All</span></button> */}
                                      </div>
                                      <div className="filter filter--matches px-3 py-2 p-md-3">
                                          <div className="filter--title mb-2 d-flex align-items-center justify-content-between">
                                            <span>
                                              <Stats
                                                translations={{
                                                  rootElementText: ({ nbHits }) =>
                                                    `${nbHits} match${nbHits === 1 ? '' : 'es'}`,
                                                }}
                                              />          
                                            </span>                  
                                        {<ClearFilters />}
                                          </div>
                                          <CurrentRefinements transformItems={transformRefinementItems} />
                                      </div>

                                      <div className="filter filter--border filter--price py-md-3 px-3 py-2 pb-3">
                                          <div className="filter--title mb-2">
                                              Price ($)
                                          </div>
                                          <div className="">
                                              <PriceRangeSlider attribute="price" />
                                          </div>
                                      </div>

                                      <div className="filter filter--border filter--price py-md-3 px-3 py-2 pb-3">
                                          <div className="filter--title mb-2">
                                              Age
                                          </div>

                                          <div className="">
                                              <AgeRangeSlider attribute="fill_date_timestamp" />
                                          </div>
                                      </div>

                                      <div className="filter filter--border filter--price py-md-3 py-2">
                                          <div className="">
                                            <Collapsible title="Spirit Type">
                                                  <RefinementList
                                                    attribute="ttb_type"
                                                    searchablePlaceholder="Search Spirit Type"
                                                    transformItems={(items) =>
                                                      items.filter(item => item.value && item.value.trim() !== '')
                                                    }
                                                  />
                                            </Collapsible>
                                          </div>
                                      </div>

                                      <div className="filter filter--border filter--proof py-md-3 py-2">
                                          <div className="spiritAge-range">
                                            <Collapsible title="Proof">
                                                <RangeInput attribute="proof" />
                                            </Collapsible>
                                          </div>
                                      </div>

                                      <div className="filter filter--border filter--price py-md-3 py-2">
                                          <div className="">
                                              <Collapsible title="Producers">
                                                <RefinementList
                                                  attribute="producer"
                                                  searchablePlaceholder="Search Producer"
                                                  transformItems={(items) =>
                                                    items.filter(item => item.value && item.value.trim() !== '')
                                                  }
                                                />
                                              </Collapsible>
                                          </div>
                                      </div>

                                      {/* <FacetDropdown
                                  closeOnChange={closeOnChange}
                                  buttonText={({ refinements }) => {
                                      const label = refinements[0]?.label.split(' > ').pop();
                                      return label ? `Origin (${label})` : `Origin`;
                                  }}
                                  classNames={{ root: 'my-BrandDropdown' }}> */}
                                      <div className="filter filter--border filter--price py-md-3 py-2">
                                          <div className="">
                                              <Collapsible title="Location">
                                                <RefinementList
                                                  attribute="facility"
                                                  searchablePlaceholder="Search Facility"
                                                  transformItems={(items) =>
                                                    items.filter(item => item.value && item.value.trim() !== '')
                                                  }
                                                />
                                              </Collapsible>
                                          </div>
                                      </div>
                                      {/* </FacetDropdown> */}
                                      <div className="filter filter--border filter--price py-md-3 py-2">
                                          <div className="">
                                              <Collapsible title="Mash bill">
                                                <RefinementList
                                                  attribute="mash_bill"
                                                  searchablePlaceholder="Search Mashbill"
                                                  transformItems={(items) =>
                                                    items.filter(item => item.value && item.value.trim() !== '')
                                                  }
                                                />
                                              </Collapsible>
                                          </div>
                                      </div>
                                      {/* <FacetDropdown
                                  buttonText={({ refinements }) => {
                                      // const label = refinements[0]?.label.split(' > ').pop();
                                      const count = refinements.length;
                                      return count ? `Liquor (${count})` : `Liquor`;
                                  }}
                                  closeOnChange={closeOnChange}
                                  classNames={{ root: 'my-BrandDropdown' }}> */}
                                      <div className="filter filter--border filter--price py-md-3 py-2">
                                          <div className="">                                            
                                              <Collapsible title="Production State">
                                                <RefinementList
                                                  attribute="state"
                                                  searchablePlaceholder="Search Production State"
                                                  transformItems={(items) =>
                                                    items.filter(item => item.value && item.value.trim() !== '')
                                                  }
                                                />
                                              </Collapsible>
                                          </div>
                                      </div>
                                      <div className="filter filter--border filter--price py-md-3 py-2">
                                          <div className="cursor-pointer">
                                              <Collapsible title="Relocation">
                                                <RefinementList
                                                  attribute="is_relocation_required"
                                                  transformItems={items =>
                                                    items
                                                    .filter(item => item.value && item.value.trim() !== '')
                                                    .map(item => ({
                                                        ...item,
                                                        label: item.label === 'true' ? 'Relocation required' : item.label === 'false' ? 'Relocation not required' : item.label
                                                    }))
                                                  } 
                                                />
                                              </Collapsible>
                                          </div>
                                      </div>

                                      <div className="filter filter--border filter--price py-md-3 py-2">
                                          <div className="cursor-pointer">
                                              <Collapsible title="Make an offer">
                                                <RefinementList
                                                  attribute="is_make_an_offer"
                                                  transformItems={items =>
                                                    items
                                                    .filter(item => item.value && item.value.trim() !== '')
                                                    .map(item => ({
                                                        ...item,
                                                        label: item.label === 'true' ? 'Available for offer' : item.label === 'false' ? 'Not available for offer' : item.label
                                                    }))
                                                  }
                                                />
                                              </Collapsible>
                                          </div>
                                      </div>

                                      <div className="filter filter--border filter--price py-md-3 py-2">
                                          <div className="cursor-pointer">
                                              <Collapsible title="Container Condition">
                                                <RefinementList
                                                  attribute="container_cooperage"
                                                  transformItems={items =>
                                                    items
                                                    .filter(item => item.value && item.value.trim() !== '')
                                                    .map(item => ({
                                                        ...item,
                                                        label: item.label === 'NEW' ? 'New' : item.label === 'REUSED' ? 'Reused' : item.label
                                                    }))
                                                  } 
                                                />
                                              </Collapsible>
                                          </div>
                                      </div>

                                      {/* <div className="filter filter--border filter--price py-md-3 px-3 py-2">
                                          <div className="cursor-pointer">
                                              <RefinementList attribute="is_account_transfer" transformItems={items =>
                                                  items
                                                  .filter(item => item.value && item.value.trim() !== '')
                                                  .map(item => ({
                                                      ...item,
                                                      label: item.label === 'true' ? 'Account transfer required' : item.label === 'false' ? 'Account transfer not required' : item.label
                                                  }))
                                              } />
                                          </div>
                                      </div> 

                                      <div className="filter filter--border filter--price py-md-3 px-3 py-2">
                                          <div className="cursor-pointer">
                                              <RefinementList attribute="is_third_party_storage" transformItems={items =>
                                                  items
                                                  .filter(item => item.value && item.value.trim() !== '')
                                                  .map(item => ({
                                                      ...item,
                                                      label: item.label === 'true' ? 'Stored at third party storage' : item.label === 'false' ? 'Not stored at third party storage' : item.label
                                                  }))
                                              } />
                                          </div>
                                      </div> */}
                                </div>
                              </div>
                          </div>
                          <div className="col-12 col-md-6 col-lg-9">
                              <div className="row d-flex justify-content-between align-items-center align-items-sm-end">
                                  <div className="col-auto mb-2">
                                      <h3 className='table__heading' style={{ color: "#9E874A"}}>Available Containers</h3>
                                  </div>
                                  <div className="listing-header col-auto mb-2 ms-auto d-flex flex-wrap align-items-center justify-content-end">
                                    {CommonService.isAdmin() ? (<>
                                      
                                      <div className='show-owner-wrapper'>
                                        <div className="d-flex align-items-center ms-2">
                                          <label className="toggle-switch">
                                            <input
                                              type="checkbox"
                                              checked={isShowAssociatedCompany}
                                              onChange={handleToggle}
                                            />
                                            <div
                                              className='switch-inner'
                                              style={{ backgroundColor: isShowAssociatedCompany ? '#9E874A' : '#6c757d' }}
                                            >
                                              <div
                                                className='switch-toggler'
                                                style={{ left: isShowAssociatedCompany ? '22px' : '2px' }}
                                              />
                                            </div>
                                            <span className='switch-label'>Show Associated Company</span>
                                          </label>
                                        </div>
                                      </div>
                                       </> 
                                    ) : CommonService.isUser() ? (
                                      <div className='show-owner-wrapper'>
                                        <div className="d-flex align-items-center ms-2">
                                          <label className="toggle-switch">
                                            <input
                                              type="checkbox"
                                              checked={isShowViewMyListingsCompany}
                                              onChange={handleViewMyListingToggle}
                                            />
                                            <div
                                              className='switch-inner'
                                              style={{ backgroundColor: isShowViewMyListingsCompany ? '#9E874A' : '#6c757d' }}
                                            >
                                              <div
                                                className='switch-toggler'
                                                style={{ left: isShowViewMyListingsCompany ? '22px' : '2px' }}
                                              />
                                            </div>
                                            <span className='switch-label'>View My Listings</span>
                                          </label>
                                        </div>
                                      </div>
                                    ) : null}
                                    <div className="marketplace-dropdown">
                                      <SwapVertIcon sx={{color:"#0c3344"}}/>
                                      <SortBy
                                        items={[
                                          { value: `${collectionName}/sort/price:asc`, label: 'Lowest price' },
                                          { value: `${collectionName}/sort/price:desc`, label: 'Highest price' },
                                          { value: `${collectionName}/sort/proof:asc`, label: 'Lowest proof' },
                                          { value: `${collectionName}/sort/proof:desc`, label: 'Highest proof' },
                                          { value: `${collectionName}/sort/fill_date_timestamp:desc`, label: 'Newest Year' },
                                          { value: `${collectionName}/sort/fill_date_timestamp:asc`, label: 'Oldest Year' },
                                          { value: `${collectionName}/sort/facility:asc`, label: 'Ascending facility' },
                                          { value: `${collectionName}/sort/facility:desc`, label: 'Descending facility' },
                                          { value: `${collectionName}/sort/producer:asc`, label: 'Ascending producer' },
                                          { value: `${collectionName}/sort/producer:desc`, label: 'Descending producer' },
                                          { value: `${collectionName}/sort/ttb_type:asc`, label: 'Ascending spirit type' },
                                          { value: `${collectionName}/sort/ttb_type:desc`, label: 'Descending spirit type' },
                                        ]}
                                      />
                                    </div>
                                    {
                                      !CommonService.isAdmin() && (
                                        <div className="view__type--select">
                                        <ButtonGroup>
                                          <Button variant="link"
                                            aria-label="View as" disabled
                                            className='d-none d-md-block'
                                          >
                                            View As
                                          </Button>
                                          <Button aria-label="Grid" className='ms-1 ms-sm-0 me-2'
                                            onClick={() => {
                                              switchView("grid");
                                            }} style={{ backgroundColor: gridView === true ? '#0C3344' : '#D7D7D7' }} >
                                            <GridViewIcon style={{ color: gridView === true ? 'white' : 'inherit' }} />
                                          </Button>
                                          <Button
                                            aria-label="Table"
                                            onClick={() => {
                                              switchView("table");
                                            }}
                                            style={{ backgroundColor: gridView === false ? '#0C3344' : '#D7D7D7' }} >
                                            <ListSharpIcon style={{ color: gridView === false ? 'white' : 'inherit' }} />
                                          </Button>
                                        </ButtonGroup>
                                      </div>
                                      )
                                    }
                                  </div>
                              </div>
                              <hr className='my-1' />
                              <div className="inventory__table--container">
                                {!gridView && (
                                  <><HitsTableContainer isShowAssociatedCompanies={isShowAssociatedCompany} isShowViewMyListingsCompanies={isShowViewMyListingsCompany} collectionName={collectionName}/></>
                                )}
                                {gridView && (
                                  <>
                                    <SearchContainer children={''}/>
                                    <HitsContainer isShowAssociatedCompanies={isShowAssociatedCompany} isShowViewMyListingsCompanies={isShowViewMyListingsCompany} collectionName={collectionName}/>
                                    <Pagination />
                                  </>
                                )}
                                {currentPath === '/user/wish-list' && (
                                  <SoldItem soldBarrelDetails={soldBarrels} gridView={gridView} removeFromWishList={removeFromWishListById} />
                                )}
                              </div>
                          </div>
                      </div>
                  </div>

              </InstantSearch>
            </div>
        </div>
    );
}


const SoldItem: React.FC<{ soldBarrelDetails: any[], gridView: boolean, removeFromWishList: (wishListId: any) => void }> = ({ soldBarrelDetails, gridView, removeFromWishList }) => {
  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'inventoryTitle',
      headerName: 'Spirit Type',
      minWidth: 250,
      flex: 1,
      renderCell: (params) => (
        <Stack>
          <span>{params.value}</span>
        </Stack>
      ),
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'price',
      headerName: 'Price $',
      type: 'number',
      flex: 1,
      minWidth: 250,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <div className='d-flex align-items-center h-100'>
          <div className="sold-out-label">Sold Out</div>
        </div>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <div>
          <div className="manageWishList me-2" onClick={() => removeFromWishList(params.row.id)}>
            <Tooltip title="Remove from Wishlist" arrow>
              <FavoriteIcon sx={{ cursor: "pointer", color: "red" }} />
            </Tooltip>
          </div>
        </div>
      ),
      align: 'left',
      headerAlign: 'left',
    },
  ], [removeFromWishList, soldBarrelDetails]);

  const rows = soldBarrelDetails.map((record, index) => ({
    id: record.id || `sold-barrel-${index}`,
    inventoryTitle: record.inventoryTitle,
    price: record.price,
  }));

  return (
    <div className='mt-2'>
      {soldBarrelDetails.length > 0 && (<> <h2 className='table__heading'>Sold Containers</h2>

        {gridView && (<div className="ais-Hits">
          <div className="ais-Hits-list">
            {soldBarrelDetails.map((barrel, index) => (
              <div className="ais-Hits-item" key={index}>
                <div className="grid-view inventory__grid-view">
                  <div className="grid-item">
                    <div className="card bg-transparent">
                      <img
                        src={WhiskeyBarrel}
                        className="card-img-top"
                        alt="Barrel"
                      />
                      <div className="card-body">
                        <div className="card-title">
                          <div className='d-flex justify-content-between align-items-start'>
                            <h5>
                              {barrel.inventoryTitle}
                            </h5>
                          </div>
                        </div>

                        <div className='d-flex justify-content-center position-relative'>
                          <span onClick={() => removeFromWishList(barrel?.id)} className='position-absolute top-0' style={{ right: 10 }}>
                            <Tooltip title="Remove from Wishlist" arrow>
                              <FavoriteIcon sx={{ cursor: "pointer", color: "red" }} />
                            </Tooltip>
                          </span>
                          <div className="sold-out-label mb-3">Sold Out</div>

                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>)}

        {!gridView && (<div className="table-container my-3">
          <DataGrid
            rows={rows}
            columns={columns}
            pageSizeOptions={[10, 20, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 50 },
              },
            }}
            rowHeight={70}
            className='data-grid marketplace-list-table'
            disableColumnResize
            disableColumnMenu
          />
        </div>)}
      </>
      )}
    </div>
  );
};
  
export default Typesense;